/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../../components/dashboard';
import { Button, Input } from 'reactstrap';
import IconButton from '../../../components/button/IconButton';
import { BlockingLoader } from '../../../components/loader/BlockingLoader';
import { AssessmentInput, AssessmentScores, useGenerateScoresTestingMutation } from '../api/materiality-assessment';
import { useSiteAlert } from '../../../hooks/useSiteAlert';
import { QUESTION } from '../../../constants/terminology';
import { AssessmentResults } from './assessment/AssessmentResults';
import { getV6Router } from '../../common/utils/V6Router';
import { V6Route } from '../../common/types/V6Router';
import { useNavigate } from 'react-router-dom-v5-compat';

type QuestionAnswerInput = { q: string, a: string };

const convertToInputs = (inputs: QuestionAnswerInput[]): AssessmentInput => {
  const preparedInputs: AssessmentInput = {};
  inputs.forEach(({ q, a }) => {
    if (q === '') {
      return;
    }
    if (!preparedInputs[q]) {
      preparedInputs[q] = [];
    }
    preparedInputs[q].push(a);
  });
  return preparedInputs;
}

const convertFromInputs = (assessmentInputs: AssessmentInput): QuestionAnswerInput[] => {
  const inputs: QuestionAnswerInput[] = [];
  Object.keys(assessmentInputs).forEach(q => {
    const answers = assessmentInputs[q];
    answers.forEach(a => {
      inputs.push({ q, a });
    });
  });
  return inputs;
}

const initialInputs: QuestionAnswerInput[] = [
  {
    q: 'sector',
    a: '60101010'
  },
  {
    q: 'org_structure',
    a: 'green'
  },
  {
    q: 'num_staff',
    a: 'orange'
  },
  {
    q: 'annual_sales',
    a: 'orange'
  },
  {
    q: 'capital_employed',
    a: 'orange'
  },
  {
    q: 'capital_employed',
    a: 'red'
  }
];

export const MaterialityAssessmentTestingTool = () => {

  const { addSiteError } = useSiteAlert();
  const [inputs, setInputs] = useState<QuestionAnswerInput[]>(initialInputs);
  const [scores, setScores] = useState<AssessmentScores | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [getScoresMutation] = useGenerateScoresTestingMutation();
  const navigate = useNavigate();

  const addInput = () => setInputs(state => [...state, { q: '', a: '' }]);

  const removeInput = (ix: number) => {
    setInputs(state => state.filter((_, i) => i !== ix));
  }

  const updateInput = (ix: number, q: string, a: string) => {
    setInputs(state => {
      const newState = [...state];
      newState[ix] = { q, a };
      return newState;
    });
  }

  const handleBackToList = () => {
    const router = getV6Router();
    const url = router.generateUrl(V6Route.MaterialityAssessmentRoot);
    navigate(url);
  }

  const getScores = () => {
    setLoading(true);
    const assessmentInputs = convertToInputs(inputs);

    getScoresMutation({ inputs: assessmentInputs })
      .unwrap()
      .then(d => {
        setScores(d.scores);
        setInputs(convertFromInputs(assessmentInputs));
      })
      .catch(e => {
        addSiteError(e.message)
      })
      .finally(() => {
        setLoading(false)
      });
  }

  return (
    <Dashboard>
      {isLoading ? <BlockingLoader /> : null}
      <DashboardRow>
        <div className='d-flex flex-fill align-items-center'>
          <div className='me-auto'>
            <Button color='link' onClick={handleBackToList}>
              <i className='fal fa-chevron-left me-2' />
              Back to assessment list
            </Button>
          </div>
        </div>
      </DashboardRow>
      <DashboardSectionTitle
        title={'Advanced testing tool'}
      />
      <DashboardSection
        title={'Inputs'}
      >
        <>
          <div className='d-flex strong'>
            <div className='w-25 pr-1'>
              {QUESTION.CAPITALIZED_SINGULAR} Code
            </div>
            <div className='flex-fill pr-1'>
              Answer Code
            </div>
          </div>
          {inputs.map((input, ix) => <QuestionAnswer
            key={`q_${ix}`}
            {...input}
            ix={ix}
            handleRemove={() => removeInput(ix)}
            handleUpdate={updateInput} />
          )}
        </>

        <div className='d-flex mt-5'>
          <Button className='mr-auto' onClick={() => addInput()}>
            <i className='fa fa-plus mr-2' />Add another input
          </Button>
          <Button color='primary' onClick={() => getScores()} disabled={Object.keys(inputs).length === 0}>
            <i className='fa fa-sparkles mr-2' />Generate materiality scores
          </Button>
        </div>

        {scores ?
          <>
            <hr />
            <h5 className='mt-6'>Scores</h5>
            <AssessmentResults scores={scores} />
          </>
          : null}
      </DashboardSection>
    </Dashboard>
  );
};

interface QuestionAnswerProps extends QuestionAnswerInput {
  ix: number;
  handleUpdate: (ix: number, q: string, a: string) => void;
  handleRemove: () => void;
}

const QuestionAnswer = (props: QuestionAnswerProps) => {
  const { ix, q, a, handleRemove, handleUpdate } = props;

  const clean = (txt: string) => txt.replaceAll(' ', '');

  return (
    <div className='d-flex mt-1'>
      <div className='w-25 pr-1'>
        <Input value={q} onChange={e => handleUpdate(ix, clean(e.target.value), a)} />
      </div>
      <div className='flex-fill pr-1'>
        <Input value={a} onChange={e => handleUpdate(ix, q, clean(e.target.value))} />
      </div>
      <div className='flex-shrink'>
        <IconButton icon='fa-trash' color='link-secondary' onClick={() => handleRemove()} />
      </div>
    </div>
  );
}
